import {
  Box,
  Button,
  Checkbox,
  Collapse,
  ExpandMoreIcon,
  FormControlLabel,
  Grid,
  makeStyles,
  MenuItem,
  TextField,
  Theme,
  Typography,
} from '@freighthub/silhouette-core'
import cn from 'classnames'
import { FC, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useI18n } from 'utils/localisation'

import { MQFields } from '../ManualQuoteDialog/MQFormContentProcessor'

export const testIds = {
  additionalRequirements: 'manual_quote-additional_fields-section',
  additionalRequirementsToggleBtn: 'manual_quote-additional_fields-toggle_btn',
}

const useStyles = makeStyles<Theme>((theme) => ({
  additionalRequirementsTitle: {
    width: '100%',
    paddingLeft: 0,
    marginTop: theme.spacing(1),
  },
  expand: {
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    marginLeft: 'auto',
    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(-1),
    },
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
}))

export type ManualQuoteAdditionalFieldsTitleProps = {
  onExpandClick: () => void
  expanded?: boolean
}

export const ManualQuoteAdditionalFieldsTitle: FC<ManualQuoteAdditionalFieldsTitleProps> = ({
  onExpandClick,
  expanded,
}) => {
  const classes = useStyles()
  const { translate } = useI18n()
  return (
    <Button
      onClick={onExpandClick}
      className={classes.additionalRequirementsTitle}
      data-testid={testIds.additionalRequirementsToggleBtn}
    >
      <Typography variant="h6" component="h3">
        {translate('manual-quote-additional_requirements')}
      </Typography>
      <ExpandMoreIcon
        className={cn(classes.expand, {
          [classes.expandOpen]: expanded,
        })}
      />
    </Button>
  )
}

const transportToPortModeOptions: Record<'value' | 'label', string>[] = [
  {
    value: 'truck',
    label: 'manual-quote-transport_to_port_mode_truck',
  },
  {
    value: 'intermodal',
    label: 'manual-quote-transport_to_port_mode_inter_modal',
  },
  {
    value: 'barge',
    label: 'manual-quote-transport_to_port_mode_barge',
  },
]

type ManualQuoteAdditionalFieldsProps = {
  defaultIsExpanded?: boolean
}
export const ManualQuoteAdditionalFields: FC<ManualQuoteAdditionalFieldsProps> = ({ defaultIsExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultIsExpanded)
  const { translate } = useI18n()
  const { control } = useFormContext<MQFields>()

  const onExpandClick = () => {
    setExpanded(!expanded)
  }

  return (
    <Box>
      <ManualQuoteAdditionalFieldsTitle onExpandClick={onExpandClick} expanded={expanded} />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Grid container spacing={2} data-testid={testIds.additionalRequirements}>
          <Grid xs={12} md={6}>
            <FormControlLabel
              label={translate('manual-quote-heavy_tested_container')}
              control={
                <Controller
                  control={control}
                  name="additionalRequirements.isHeavyTestedContainer"
                  render={({ field: { value, ...field } }) => <Checkbox {...field} checked={value} color="primary" />}
                />
              }
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormControlLabel
              label={translate('manual-quote-letter_of_credit_required')}
              control={
                <Controller
                  control={control}
                  name="additionalRequirements.letterOfCreditRequired"
                  render={({ field: { value, ...field } }) => <Checkbox {...field} checked={value} color="primary" />}
                />
              }
            />
          </Grid>
          <Grid xs={12} md={6}>
            <Controller
              control={control}
              name="additionalRequirements.maxPayloadContainer"
              render={({ field }) => (
                <TextField {...field} label={translate('manual-quote-max_payload_container')} fullWidth />
              )}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <Controller
              control={control}
              name="additionalRequirements.maxDemurrageTime"
              render={({ field }) => (
                <TextField {...field} label={translate('manual-quote-max_demurrage_time')} fullWidth />
              )}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <Controller
              control={control}
              name="additionalRequirements.EXWDate"
              render={({ field }) => <TextField {...field} label={translate('manual-quote-exw_date')} fullWidth />}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <Controller
              control={control}
              name="additionalRequirements.transportToPortMode"
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label={translate('manual-quote-transport_to_port_mode')}
                  fullWidth
                  data-testid="transportToPortMode"
                >
                  {transportToPortModeOptions.map(({ label, value }) => (
                    <MenuItem key={label} value={value}>
                      {translate(label)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid xs={12}>
            <Controller
              control={control}
              name="additionalRequirements.transportCompanyPreference"
              render={({ field }) => (
                <TextField {...field} label={translate('manual-quote-preferences_in_transport_company')} fullWidth />
              )}
            />
          </Grid>
        </Grid>
      </Collapse>
    </Box>
  )
}
